import React from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import CheckIcon from '@material-ui/icons/Check';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker
} from '@material-ui/pickers';

import { makeStyles } from '@material-ui/core/styles';

import { getTaxPercent } from '../common';

const useStyles = makeStyles(theme => ({
    form: {
        '& .MuiFormControl-root': {
            margin: theme.spacing(1),
            '& .MuiInput-root': {
                marginRight: theme.spacing(3)
            }
        },
        maxWidth: 650,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    calculatedText: {
        color: theme.palette.primary.main
    }
}));

const InvoiceForm = (props) => {
    const {
        fees,
        couple,
        handleSaveButtonClick,
        invoiceId,
        invoiceNumber
    } = props;

    const parseNum = num => parseFloat(num || 0);
    const formatNumber = number => {
        return parseNum(number).toFixed(2);
    }

    const classes = useStyles();

    const isCreate = invoiceId == undefined;

    if (invoiceId && couple === null) {
        return (
            <p>No couple found for invoice</p>
        )
    }

    const taxPercent = getTaxPercent(couple);
    const taxPercentLabel = taxPercent ? ` (${taxPercent*100}%)` : '';

    let defaultDueDate = new Date(couple.wedding_date*1000 - (21 * 24 * 3600000)); /* 21 days before wedding date */
    if (couple.wedding_type == 1) {
        defaultDueDate = new Date(couple.wedding_date*1000 - (3 * 24 * 3600000)); /* 3 before wedding date for elopements */
    }
    const currentTime = new Date();
    if (defaultDueDate < currentTime) {
        defaultDueDate = currentTime;
    }

    const [balance, setBalance] = React.useState(couple.balance);
    const coupleNames = couple ? `${couple.bride_first_name} & ${couple.groom_first_name}` : '';

    const [state, setState] = React.useState({
        couple_id: couple.id,
        amount_1: formatNumber(fees.amount_1),
        comments_1: fees.comments_1 || '',
        amount_2: formatNumber(fees.amount_2),
        comments_2: fees.comments_2 || '',
        amount_3: formatNumber(fees.amount_3),
        comments_3: fees.comments_3 || '',
        sub_total: formatNumber(fees.sub_total),
        tax: formatNumber(fees.tax),
        total: formatNumber(fees.total),
        date_issued: fees.date_issued ? new Date(fees.date_issued) : currentTime,
        date_due: fees.date_due ? new Date(fees.date_due) : defaultDueDate,
    });

    const [savePending, setSavePending] = React.useState(false);
    const [saveComplete, setSaveComplete] = React.useState(false);

    const handleChange = name => event => {
        let value;
        if (name == 'date_issued' || name == 'date_due') {
            value = event;
        } else {
            value = event.target.value;
        }
        updateState(value, name);
    }

    const updateState = (value, name) => {
        const data = {
            ...state,
            [name]: value,
        };
        if (name == 'comments_1' || name == 'comments_2' || name == 'comments_3') {
            setState(data);
            return;
        }

        const subTotal = formatNumber(
            parseNum(data.amount_1)
            + parseNum(data.amount_2)
            + parseNum(data.amount_3)
        );
        const tax = formatNumber(taxPercent * parseNum(subTotal));
        const total = formatNumber(parseNum(tax) + parseNum(subTotal));
        setState({
            ...data,
            sub_total: subTotal,
            tax: tax,
            total: total
        });
    }

    const handleSubmit = () => {
        const formData = {
            ...state,
            _token: window._token
        };
        new Promise((resolve, reject) => {
            /* TODO: make successful save open modal or something to take them somewhere */
            const url = isCreate ? `/api/admin/invoices` : `/api/admin/invoices/${invoiceId}`;
            setSavePending(true);
            fetch(url, {
                method: isCreate ? 'POST' : 'PUT',
                body: JSON.stringify(formData),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setSaveComplete(true);
                    setSavePending(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                        handleSaveButtonClick();
                    }, 1200);
                    if (result.balance !== undefined) {
                        setBalance(result.balance);
                    }
                    resolve();
                })
                .catch(reason => {
                    setSavePending(false);
                    alert('Please make sure all required fields have been entered.');
                })
        });
    }

    return (
        <form className={classes.form}>
            <Box mb={3}>
                {!isCreate && invoiceNumber &&
                    <Typography variant="h6">Edit Invoice #{invoiceNumber}</Typography>
                }
                <Typography variant="overline" component="p" style={{ lineHeight: 2.2 }}>{coupleNames} - {couple.wedding_date_formatted}</Typography>
                <Typography variant="overline" component="p" style={{ lineHeight: 1.5 }}>Current balance: <strong>${formatNumber(balance)}</strong></Typography>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-1">Line Item 1 (pre-tax)</InputLabel>
                        <Input
                            id="line-item-1"
                            value={state.amount_1 || ''}
                            onChange={handleChange('amount_1')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-1-comments">Line Item 1 Comments</InputLabel>
                        <Input
                            id="line-item-1-comments"
                            value={state.comments_1 || ''}
                            onChange={handleChange('comments_1')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="text"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-2">Line Item 2 (pre-tax)</InputLabel>
                        <Input
                            id="line-item-2"
                            value={state.amount_2 || ''}
                            onChange={handleChange('amount_2')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-2-comments">Line Item 2 Comments</InputLabel>
                        <Input
                            id="line-item-2-comments"
                            value={state.comments_2 || ''}
                            onChange={handleChange('comments_2')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="text"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-3">Line Item 3 (pre-tax)</InputLabel>
                        <Input
                            id="line-item-3"
                            value={state.amount_3 || ''}
                            onChange={handleChange('amount_3')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="line-item-3-comments">Line Item 3 Comments</InputLabel>
                        <Input
                            id="line-item-3-comments"
                            value={state.comments_3 || ''}
                            onChange={handleChange('comments_3')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="text"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="fees-sub-total" className={classes.calculatedText}>Sub Total</InputLabel>
                        <Input
                            id="fees-sub-total"
                            value={state.sub_total || 0}
                            onChange={handleChange('sub_total')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                            inputProps={ { readOnly: true } }
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel htmlFor="fees-tax" className={classes.calculatedText}>{`Tax${taxPercentLabel}`}</InputLabel>
                        <Input
                            id="fees-tax"
                            value={state.tax || 0}
                            onChange={handleChange('tax')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                            inputProps={ { readOnly: true } }
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel htmlFor="fees-total" className={classes.calculatedText}>Total</InputLabel>
                        <Input
                            id="fees-total"
                            value={state.total || 0}
                            onChange={handleChange('total')}
                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                            type="number"
                            inputProps={ { readOnly: true } }
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            margin="normal"
                            id="date-issued"
                            label="Date Issued"
                            format="MM/dd/yyyy"
                            fullWidth
                            value={state.date_issued}
                            onChange={handleChange('date_issued')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            margin="normal"
                            id="date-due"
                            label="Date Due"
                            format="MM/dd/yyyy"
                            fullWidth
                            value={state.date_due}
                            onChange={handleChange('date_due')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
            </Grid>
            <Box mt={4} mb={3}>
                <Grid
                    container
                    direction="row"
                    justify="center"
                    alignItems="center"
                >
                    <Grid item xs={8} sm={4}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth={true}
                            onClick={handleSubmit}
                            disabled={savePending ? true : false}
                            endIcon={savePending ? <CircularProgress size={20} /> : (saveComplete ? <CheckIcon /> : null)}
                        >
                            {isCreate ? 'Create Invoice' : 'Edit Invoice'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </form>
    )
}

export default InvoiceForm;
